import AbstractUnit from './AbstractUnit';
import { COLOR_THEMES } from '../../../../constants/colors';
import Text3D from '../../objects/Text3D';
import { REF_SIZE_PT, SIZE_OPTIONS, UNIT } from '../../../../constants/size';
import LinesSet from '../../objects/LinesSet';

const THREE = require('three');

const formatSectionIndex = (index, minNumSymbols = 2) => {
    const str = (index + 1).toString(10);

    if (str.length < minNumSymbols) {
        return `${new Array(minNumSymbols - str.length).fill(0).join('')}${str}`;
    }

    return index.toString(10);
};

class HeaderUnit extends AbstractUnit {
    constructor(data, webgl, stgs, markers) {
        super(data, webgl, stgs, null, markers);
    }

    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        const {
            theme, index, title, titlePlane,
            subtitle, subtitlePlane, subtitleWidthEM
        } = this.data;

        const {
            getTitleMetrics, getSubMetrics,
            needIndex, needArticleHeaderResize
        } = stgs;
        const ttl = titlePlane ?? title;

        this.hasResize = true;
        this.needArticleHeaderResize = needArticleHeaderResize;
        this._color = COLOR_THEMES[theme].text;
        this._title = needIndex ? `${formatSectionIndex(index)}.\n${ttl}` : ttl;
        this._subtitle = subtitlePlane ?? subtitle;
        this._subtitleWidthEM = subtitleWidthEM;
        this._getTitleMetrics = getTitleMetrics;
        this._getSubMetrics = getSubMetrics;

        // Default
        this._orientation = SIZE_OPTIONS.LANDSCAPE;
    }

    _constructView(data, webgl, stgs, factory, orientation) {
        const root = new THREE.Group();

        const titleMesh = this._getTextMesh(
            this._getTitleMetrics(orientation),
            this._title
        );

        const subtitleG = new THREE.Group();
        const subtitleMesh = this._getTextMesh(
            this._getSubMetrics(orientation, this._subtitleWidthEM),
            this._subtitle
        );

        let linesTop;
        let linesBottom;

        if (this.needArticleHeaderResize) {
            linesTop = new LinesSet(this._color, 145, 14, 0.5);
            linesBottom = new LinesSet(this._color, 145, 14, 0.5);
        } else {
            linesTop = new LinesSet(this._color);
            linesBottom = new LinesSet(this._color);
        }

        linesBottom.rotation.z = Math.PI;

        root.position.z = -this.mainViewZ;

        subtitleG.add(subtitleMesh);
        subtitleG.add(linesTop);
        subtitleG.add(linesBottom);

        root.add(titleMesh);
        root.add(subtitleG);
        this.add(root);

        const shiftRenderOrder = -8;

        root.renderOrder = 15 + shiftRenderOrder;
        titleMesh.renderOrder = 15 + shiftRenderOrder;
        subtitleG.renderOrder = 14 + shiftRenderOrder;
        subtitleMesh.renderOrder = 14 + shiftRenderOrder;
        linesTop.renderOrder = 13 + shiftRenderOrder;
        linesBottom.renderOrder = 13 + shiftRenderOrder;

        this.titleMesh = titleMesh;
        this.subtitleMesh = subtitleMesh;
        this.subtitleG = subtitleG;
        this.linesTop = linesTop;
        this.linesBottom = linesBottom;
        this.mainView = root;

        this._updatePositions(this._orientation);
    }

    _getTextMesh(metrics, text) {
        return new Text3D({
            ...metrics,
            color: this._color
        }, text, this.webgl);
    }

    resize(winWidth, winHeight, orientation, force) {
        if (this._orientation === orientation && !force) return;

        this.titleMesh.setMetrics({
            ...this._getTitleMetrics(orientation)
        });

        this.subtitleMesh.setMetrics({
            ...this._getSubMetrics(orientation, this._subtitleWidthEM)
        });

        this._updatePositions(orientation);

        this._orientation = orientation;
    }

    _updatePositions(sizeOption) {
        if (this.needArticleHeaderResize) {
            this._updateArticleHeaderPositions(sizeOption);

            return;
        }

        this._updateSectionHeaderPositions(sizeOption);
    }

    _updateArticleHeaderPositions(sizeOption) {
        // subtitle
        let obj3D = this.subtitleG;

        const { textHeight, lineHeight } = this.subtitleMesh;
        const ttlLineHeight = this.titleMesh.lineHeight;

        obj3D.position.y = -textHeight * 0.5 - lineHeight * 3 - ttlLineHeight * 0.5;

        const yFromCenter = textHeight * 0.5 + lineHeight * 1.5;
        let scale = sizeOption === SIZE_OPTIONS.LANDSCAPE ? 1 : 2;

        scale *= UNIT.sizesScaleRatio;

        obj3D = this.linesTop;
        obj3D.position.y = yFromCenter;
        obj3D.scale.set(scale, scale, 1);

        obj3D = this.linesBottom;
        obj3D.position.y = -lineHeight * 0.8 - yFromCenter;
        obj3D.scale.set(scale, scale, 1);

        // title
        obj3D = this.titleMesh;
        if (sizeOption === SIZE_OPTIONS.LANDSCAPE) {
            obj3D.position.y = obj3D.textHeight * 0.5 - ttlLineHeight * 0.1;
            this.mainView.scale.set(0.9, 0.9, 1);

            return;
        }

        obj3D.position.y = REF_SIZE_PT.h * UNIT.sizesScaleRatio * 0.4;
        this.mainView.scale.set(1, 1, 1);
    }

    _updateSectionHeaderPositions(sizeOption) {
        let obj3D = this.titleMesh;

        obj3D.position.y = obj3D.textHeight * 0.5 - obj3D.lineHeight * 0.1;

        const { textHeight, lineHeight } = this.subtitleMesh;

        obj3D = this.subtitleG;
        obj3D.position.y = -textHeight * 0.5 - lineHeight * 3;

        const yFromCenter = textHeight * 0.5 + lineHeight * 0.5;
        let scale = sizeOption === SIZE_OPTIONS.LANDSCAPE ? 1 : 2;

        scale *= UNIT.sizesScaleRatio;

        obj3D = this.linesTop;
        obj3D.position.y = yFromCenter;
        obj3D.scale.set(scale, scale, 1);

        obj3D = this.linesBottom;
        obj3D.position.y = -lineHeight * 0.8 - yFromCenter;
        obj3D.scale.set(scale, scale, 1);
    }
}

export default HeaderUnit;
